import React from 'react';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaEyeSlash } from '@react-icons/all-files/fa/FaEyeSlash';
import { FaLock } from '@react-icons/all-files/fa/FaLock';
import ProfaseModal from '../../commons/ProfaseModal';

export interface ActivityVisibilityItem {
  id: number;
  label: string;
  bloqueado: boolean;
}

interface WorkflowActivitiesVisibilityModalProps {
  open: boolean;
  activities: ActivityVisibilityItem[];
  draftHiddenIds: Set<number>;
  draftLockedIds: Set<number>;
  onToggleVisible: (id: number) => void;
  onToggleLocked: (id: number) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onLockAll: () => void;
  onUnlockAll: () => void;
  onApply: () => void;
  onClose: () => void;
}

export default function WorkflowActivitiesVisibilityModal({
  open,
  activities,
  draftHiddenIds,
  draftLockedIds,
  onToggleVisible,
  onToggleLocked,
  onShowAll,
  onHideAll,
  onLockAll,
  onUnlockAll,
  onApply,
  onClose,
}: WorkflowActivitiesVisibilityModalProps) {
  const hiddenCount = draftHiddenIds.size;
  const lockedCount = draftLockedIds.size;

  return (
    <ProfaseModal
      open={open}
      titleId="workflow-activities-visibility-modal-title"
      size="wide"
      scroll
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={s.header}>
        <h2 id="workflow-activities-visibility-modal-title" className="profase-modal-title m-0">
          <FaEye style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Visibilidad de actividades
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cancelar
        </button>
      </div>

      <p style={s.description}>
        Al ocultar una actividad se bloquea automáticamente (puede desbloquearla aunque siga oculta).
        Los cambios se aplican al pulsar <strong>Aplicar</strong>.
      </p>

      <div style={s.bulkActions}>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onShowAll}
          disabled={activities.length === 0 || hiddenCount === 0}
        >
          Mostrar Todo
        </button>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onHideAll}
          disabled={activities.length === 0 || hiddenCount === activities.length}
        >
          Ocultar Todo
        </button>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onLockAll}
          disabled={activities.length === 0 || lockedCount === activities.length}
        >
          Bloquear Todo
        </button>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={onUnlockAll}
          disabled={activities.length === 0 || lockedCount === 0}
        >
          Desbloquear Todo
        </button>
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: 72 }}>ID</th>
              <th style={s.th}>Actividad</th>
              <th style={{ ...s.th, width: 72, textAlign: 'center' }}>Visible</th>
              <th style={{ ...s.th, width: 72, textAlign: 'center' }}>Bloqueado</th>
            </tr>
          </thead>
          <tbody>
            {activities.length === 0 ? (
              <tr>
                <td colSpan={4} style={s.emptyCell}>Sin actividades</td>
              </tr>
            ) : activities.map(item => {
              const visible = !draftHiddenIds.has(item.id);
              const locked = draftLockedIds.has(item.id);
              return (
                <tr key={item.id}>
                  <td style={s.td}>{item.id}</td>
                  <td style={s.td}>{item.label}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button
                      type="button"
                      style={{
                        ...s.iconBtn,
                        ...(visible ? s.eyeBtnVisible : s.eyeBtnHidden),
                      }}
                      title={visible ? 'Ocultar actividad en la grilla' : 'Mostrar actividad en la grilla'}
                      aria-label={visible ? `Ocultar ${item.label}` : `Mostrar ${item.label}`}
                      aria-pressed={visible}
                      onClick={() => onToggleVisible(item.id)}
                    >
                      {visible ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                    </button>
                  </td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button
                      type="button"
                      style={{
                        ...s.iconBtn,
                        ...(locked ? s.lockBtnActive : s.lockBtnInactive),
                      }}
                      title={locked ? 'Desbloquear actividad' : 'Bloquear actividad'}
                      aria-label={locked ? `Desbloquear ${item.label}` : `Bloquear ${item.label}`}
                      aria-pressed={locked}
                      onClick={() => onToggleLocked(item.id)}
                    >
                      <FaLock size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={s.footer}>
        <span style={s.summary}>
          {hiddenCount > 0
            ? `${hiddenCount} oculta${hiddenCount === 1 ? '' : 's'}`
            : 'Todas visibles'}
          {' · '}
          {lockedCount > 0
            ? `${lockedCount} bloqueada${lockedCount === 1 ? '' : 's'}`
            : 'Ninguna bloqueada'}
        </span>
        <div style={s.footerActions}>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="profase-form-btn-primary btn-sm" onClick={onApply}>
            Aplicar
          </button>
        </div>
      </div>
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  description: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
    lineHeight: 1.45,
  },
  bulkActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  tableWrap: {
    maxHeight: 'min(420px, 55vh)',
    overflow: 'auto',
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 8,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  th: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--crm-grid-header-text, #334155)',
    background: 'var(--crm-grid-header-bg, #f8fafc)',
    borderBottom: '1px solid var(--crm-border, #e2e8f0)',
  },
  td: {
    padding: '8px 10px',
    fontSize: 13,
    color: 'var(--crm-body-text)',
    borderBottom: '1px solid var(--crm-border-subtle, #f1f5f9)',
    verticalAlign: 'middle',
  },
  emptyCell: {
    padding: 20,
    textAlign: 'center',
    color: 'var(--crm-text-secondary)',
    fontSize: 13,
  },
  iconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    padding: 0,
    border: '1px solid var(--crm-border, #cbd5e1)',
    borderRadius: 6,
    background: 'var(--crm-surface, #fff)',
    cursor: 'pointer',
  },
  eyeBtnVisible: {
    color: '#10b981',
  },
  eyeBtnHidden: {
    color: 'var(--crm-text-muted, #94a3b8)',
    opacity: 0.75,
  },
  lockBtnActive: {
    color: '#d97706',
  },
  lockBtnInactive: {
    color: 'var(--crm-text-muted, #94a3b8)',
    opacity: 0.55,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
    flexWrap: 'wrap',
  },
  summary: {
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
};
